
import naturalCompare from 'string-natural-compare'
import purchaseHistory from '../../purchaseHistory'
import { isSessionStorageAvailable } from '../../storage'

const searchHelper = {
    visibleProducts: [],
    cmsRecords: [],
    currentIso: window.accuvars.language.iso_code,
    productResults: [],
    startIndex: null,
    endIndex: null,
    hits: null,
    loading: false,
    loadingProducts: false,
    filters: [],
    query: '',
    initialLoad: true,
    categoryOptions: [],
    categoryOptionsLocked: false,
    filteredCategory: [],
    categoryFilters: [],
    categoryFiltersSelections: {}, // facet values
    categoryFiltersSelectionsIds: {}, // id values
    sortString: 'desc:accu_rank', // i think this will have to map to replica name per option
    expressStockOnly: false,
    pagination: {
        start: 1,
        stop: 1,
        page: 1,
        step: 16,
        pages: 1,
        previousPages: [],
    },
    pageUsedOn: 'Search Landing',
    pageUsedOnId: 'search_landing',
    filterCheckboxes: [],
    storedCategoryFilters: isSessionStorageAvailable() ? window.Alpine.$persist({ active_filters: [] }).using(sessionStorage) : { active_filters: [] },
    filtersChanged: false,
    // Parameters added by ad platforms, email and analytics. They stay in the
    // URL but are never read as filters: an unknown parameter becomes a filter
    // that matches no products. utm_* is skipped by prefix as well. Google's
    // url_passthrough copies gclid, gclsrc, dclid, wbraid, gbraid and _gl onto
    // every internal link for visitors who haven't accepted cookies.
    trackingQueryParameters: [
        // Google
        'gclid', 'gclsrc', 'dclid', 'wbraid', 'gbraid', 'gad_source', 'gad_campaignid', 'srsltid', '_gl',
        // Microsoft, Meta, Reddit, LinkedIn, TikTok, X
        'msclkid', 'fbclid', 'rdt_cid', 'li_fat_id', 'ttclid', 'twclid',
        // Klaviyo
        '_kx',
        // See changelogs/1.55.3
        '...',
    ],
    trackingQueryParametersData: [],
    updateSessionStorageForExpress() {
        const categoryDataIndex = this.getPersistedIndex()
        if (categoryDataIndex !== -1) {
            this.storedCategoryFilters.active_filters[categoryDataIndex].expressStockOnly = this.expressStockOnly
        }
    },
    changeSorting(sortString) {
        this.sortString = sortString
    },
    removeFilter(filter, loadProducts = true) {
        const [name, value] = filter.split('|')
        this.filterCheckboxes = this.filterCheckboxes.filter((f) => f !== filter)
        this.applyFilter({ name }, { feature: value }, false, loadProducts)
    },
    removeAllFilters() {
        this.filterCheckboxes = []

        window.Alpine.nextTick(() => {
            this.categoryFilters = []
            this.categoryFiltersSelections = {}
            this.categoryFiltersSelectionsIds = {}

            const categoryDataIndex = this.getPersistedIndex()
            if (categoryDataIndex !== -1) {
                this.storedCategoryFilters.active_filters[categoryDataIndex].filters = []
            }

            this.updateUrl()
            this.loadProducts(1)
        })
    },
    extractPotentialFiltersFromQuery() {
        const loopUrl = new URL(window.location.href);
        this.trackingQueryParametersData = []

        for (const [key, value] of loopUrl.searchParams) {
            // values to ignore (page and express)
            if(key === 'express' && window.helpers.getQueryVar(key) === '1') {
                // apply express stock filter
                this.expressStockOnly = true
            }

            if (this.trackingQueryParameters.includes(key) || key.startsWith('utm_')) {
                this.trackingQueryParametersData.push({ key, value })
                continue
            }

            if (key === 'page' || key === 'express' || key === 'query' || key === 'cs_ids' || key === 'manufacturer_name') {
                continue;
            }

            this.applyFilterFromQuery(
                key,
                window.helpers.getQueryVar(key).split(','),
            );
        }
    },
    applyAllFiltersFromQuery() {
        this.categoryFilters.filter((f) => window.helpers.getQueryVar(f.name))
            .forEach((f) => this.applyFilterFromQuery(
                f.name,
                window.helpers.getQueryVar(f.name).split(','),
            ))
    },
    applyFilterFromQuery(name, values) {
        const key = `${this.currentIso}.features.${name}`
        this.categoryFiltersSelections[key] = values

        values.forEach((value) => {
            if(this.filterCheckboxes.includes(`${name}|${value}`)) {
                return; // if this combination exists already , dont readd it
            }

            this.filterCheckboxes.push(`${name}|${value}`)
        })
    },
    applyAllFiltersFromStorage() {
        try {
            const categoryDataIndex = this.getPersistedIndex()

            if (categoryDataIndex !== -1 && Object.keys(this.storedCategoryFilters.active_filters[categoryDataIndex].filters).length > 0) {
                // check if filters are in same language
                if (!Object.keys(this.storedCategoryFilters.active_filters[categoryDataIndex].filters)[0].startsWith(this.currentIso)) {
                    this.storedCategoryFilters.active_filters = []
                } else {
                    this.categoryFiltersSelections = this.storedCategoryFilters.active_filters[categoryDataIndex].filters
                    Object.keys(this.categoryFiltersSelections).forEach((key) => {
                        this.categoryFiltersSelections[key].forEach((value) => {
                            if(this.filterCheckboxes.includes(`${key.substring(12)}|${value}`)) {
                                return; // if this combination exists already , dont readd it
                            }
                            this.filterCheckboxes.push(`${key.substring(12)}|${value}`)
                        })
                    })
                }
            }

            if (categoryDataIndex !== -1 && this.storedCategoryFilters.active_filters[categoryDataIndex].expressStockOnly !== undefined) {
                 this.expressStockOnly = this.storedCategoryFilters.active_filters[categoryDataIndex].expressStockOnly
            }  

            // we have buildt all needed update url now 
            if (categoryDataIndex !== -1 && 
                (this.storedCategoryFilters.active_filters[categoryDataIndex].expressStockOnly !== undefined
                || this.storedCategoryFilters.active_filters[categoryDataIndex].expressStockOnly !== this.expressStockOnly)
            ) {
                this.updateUrl()
            }

        } catch (error) {
            console.error('Error applying filters from storage:', error);
        }
    },
    applyFilter(filter, feature, checked, loadProducts = true) {
        if (!this.categoryFiltersSelections[`${this.currentIso}.features.${filter.name}`]) {
            this.categoryFiltersSelections[`${this.currentIso}.features.${filter.name}`] = []
        }
        if (checked) {
            this.categoryFiltersSelections[`${this.currentIso}.features.${filter.name}`].push(feature.feature)
        } else {
            this.categoryFiltersSelections[`${this.currentIso}.features.${filter.name}`] = this.categoryFiltersSelections[`${this.currentIso}.features.${filter.name}`].filter(
                (item) => item !== feature.feature,
            )
        }

        if (!this.categoryFiltersSelectionsIds[filter.id_feature]) {
            this.categoryFiltersSelectionsIds[filter.id_feature] = []
        }
        if (checked) {
            this.categoryFiltersSelectionsIds[filter.id_feature].push(feature.feature)
        } else {
            this.categoryFiltersSelectionsIds[filter.id_feature] = this.categoryFiltersSelectionsIds[filter.id_feature].filter(
                (item) => item !== feature.feature,
            )
        }

        // if category is not yet in local storage / add it 
        let categoryDataIndex = this.getPersistedIndex()
        if (categoryDataIndex === -1) {
            if (this.storedCategoryFilters.active_filters.length >= process.env.MIX_NUMBER_PERSIST_CATEGORY_FILLTERS) {
                this.storedCategoryFilters.active_filters.shift()
            }

            this.storedCategoryFilters.active_filters.push({
                category: this.filteredCategory[0],
                filters: {},
                expressStockOnly: false,
            })
        }

        categoryDataIndex = this.getPersistedIndex()
        if (categoryDataIndex !== -1) {
            this.storedCategoryFilters.active_filters[categoryDataIndex].filters = this.categoryFiltersSelections
            this.storedCategoryFilters.active_filters[categoryDataIndex].expressStockOnly = this.expressStockOnly
        }

        this.updateUrl()

        this.filtersChanged = true

        if (loadProducts) {
            this.loadProducts(1)
        }
    },
    clearFilters(filter, loadProducts = true) {
        if (filter) {
            this.categoryFiltersSelections[`${this.currentIso}.features.${filter.name}`] = []
            this.categoryFiltersSelectionsIds[filter.id_feature] = []
        } else {
            this.categoryFilters = []
            this.categoryFiltersSelections = {}
            this.categoryFiltersSelectionsIds = {}
        }

        const categoryDataIndex = this.getPersistedIndex()
        if (categoryDataIndex !== -1) {
            this.storedCategoryFilters.active_filters[categoryDataIndex].filters = []
        }

        if (loadProducts) {
            this.loadProducts(1)
        }
    },
    getPersistedIndex() {
        return this.storedCategoryFilters.active_filters.findIndex((categoryDataEntry, index) => {
            if (categoryDataEntry.category === this.filteredCategory[0]) {
                return true
            }

            return false
        })
    },
    numberOfResults() {
        return this.hits
    },
    hasOneCategorySelected() {
        if (this.filteredCategory.length === 1) {
            return true
        }
        return false
    },
    getCurrentPage() {
        const url = new URL(window.location.href)
        return parseInt(url.searchParams.get('page'), 10) || 1
    },
    setCurrentPage(page) {
        const url = new URL(window.location.href)
        if (page > 1) {
            url.searchParams.set('page', page)
        } else {
            url.searchParams.delete('page')
        }

        // persist page on filtered category
        if (typeof this.filteredCategory[0] !== 'undefined') {
            const categoryDataIndex = this.getPersistedIndex()

            if (categoryDataIndex !== -1) {
                this.storedCategoryFilters.active_filters[categoryDataIndex].page = page
            } else {
                if (this.storedCategoryFilters.active_filters.length >= process.env.MIX_NUMBER_PERSIST_CATEGORY_FILLTERS) {
                    this.storedCategoryFilters.active_filters.shift()
                }

                this.storedCategoryFilters.active_filters.push({
                    category: this.filteredCategory[0],
                    filters: this.categoryFiltersSelections,
                    page: page,
                    expressStockOnly: this.expressStockOnly,
                })
            }
        }

        window.history.replaceState({}, null, url.toString())
    },
    convertMetricToDecimal(value) {
        let full = 0
        let partial = 0
        if (value.includes('.')) {
            const numberPortions = value.split('.')
            full = parseInt(numberPortions[0], 10)
            if (numberPortions[1].includes('/')) {
                const bits = numberPortions[1].split('/')
                partial = parseInt(bits[0], 10) / parseInt(bits[1], 10)
            } else {
                partial = parseFloat(`0.${numberPortions[1]}`)
            }
        } else if (value.includes('/')) {
            const bits = value.split('/')
            partial = parseInt(bits[0], 10) / parseInt(bits[1], 10)
        } else {
            full = parseInt(value, 10)
        }

        return full + partial
    },
    parseImperialLength(text) {
        const parts = String(text).trim().split(/\s+/)
            .filter(Boolean)
        if (!parts.length) {
            return null
        }

        const decimals = parts.map((part) => this.convertMetricToDecimal(part))
        if (decimals.some((decimal) => Number.isNaN(decimal))) {
            return null
        }

        return decimals.reduce((sum, decimal) => sum + decimal, 0)
    },
    gaugeToInch(gauge) {
        return (gauge * 0.013) + 0.06
    },
    inchToMm(inches) {
        return inches * 25.4
    },
    gaugeToMm(gauge) {
        return this.inchToMm(this.gaugeToInch(gauge))
    },
    isThreadStandard(text) {
        const threadStandards = [
            'unc', 'unf', 'unef', 'unjc', 'unjf', 'unjef', 'uns', 'unr', 'un',
            'bsw', 'bsf', 'bsp', 'bspt', 'bspg', 'bsp(g)', 'bspp', 'bsc', 'ba',
            'npt', 'nptf', 'acme', 'sti',
        ]
        const suffix = String(text).trim()
        return threadStandards.some((standard) => suffix.startsWith(standard)
            && !/[a-z0-9]/.test(suffix.charAt(standard.length)))
    },
    toSortable(value) {
        const gaugeMinPitch = 22
        const lower = String(value).trim().toLowerCase()

        // Metric thread, sorted by major diameter (e.g. "M10 x 1.5 mm", "M3 (3 mm)")
        const metric = lower.match(/^m\s*(\d+(?:\.\d+)?)/)
        if (metric) {
            return parseFloat(metric[1])
        }

        if (lower.endsWith('mm')) {
            const mm = parseFloat(lower.slice(0, -2).trim())
            return Number.isNaN(mm) ? value : mm
        }

        if (lower.endsWith('inch')) {
            // Strip a leading BSP "G" designation (e.g. "G 1/2 inch")
            const body = lower.slice(0, -4).trim().replace(/^g\s*(?=[\d.])/, '')
            const inches = this.parseImperialLength(body)
            return inches === null ? value : this.inchToMm(inches)
        }

        // British Association (e.g. "4BA", "0 BA")
        const ba = lower.match(/^(\d+)\s*ba\b/)
        if (ba) {
            const size = parseInt(ba[1], 10)
            const pitch = Number(((0.9 ** size) ** 1.2).toPrecision(2))
            return Number((pitch * 6).toPrecision(2))
        }

        // Numbered gauge with prefix (e.g. "No.8", "#8")
        const gaugePrefixed = lower.match(/^(?:no\.?|#)\s*(\d+)\b/)
        if (gaugePrefixed) {
            return this.gaugeToMm(parseInt(gaugePrefixed[1], 10))
        }

        // Gauge or inch sizes with pitch (e.g. "2-56 UNC", "1/4-20 UNF")
        const thread = lower.match(/^(\d[\d\s/.]*?)(?:-([\d/.]+))?\s*([a-z].*)$/)
        if (thread && this.isThreadStandard(thread[3])) {
            const diameterText = thread[1].trim()
            const pitch = thread[2] ? this.convertMetricToDecimal(thread[2]) : 0

            // Gauge shares the same format as inches, but with a higher pitch (e.g. "1-72" vs "1-8")
            if (/^\d+$/.test(diameterText) && pitch >= gaugeMinPitch) {
                return this.gaugeToMm(parseInt(diameterText, 10))
            }

            // Otherwise treat it as inches
            const inches = this.parseImperialLength(diameterText)
            if (inches !== null) {
                return this.inchToMm(inches)
            }
        }

        return value
    },
    customSorting(a, b) {
        const baseA = a.trim().toLowerCase()
        const baseB = b.trim().toLowerCase()
        const endsWithModA = /\bmod$/i.test(baseA)
        const endsWithModB = /\bmod$/i.test(baseB)
        const startsWithMA = /^m\s*\d/i.test(baseA)
        const startsWithMB = /^m\s*\d/i.test(baseB)

        if (!Number.isNaN(baseA) && !Number.isNaN(baseB)) {
            const sortableA = searchHelper.toSortable(baseA)
            const sortableB = searchHelper.toSortable(baseB)

            if (sortableA !== baseA && sortableB !== baseB) {
                if (sortableA < sortableB) {
                    return -1
                }
                if (sortableA > sortableB) {
                    return 1
                }
            }
        }

        const parseNumericToken = (token) => {
            if (token.includes('/')) {
                return searchHelper.convertMetricToDecimal(token)
            }

            return parseFloat(token)
        }

        const extractNumericSequence = (value) => {
            const tokens = value.match(/\d+(?:\.\d+)?(?:\/\d+)?/g) || []
            return tokens
                .map((token) => parseNumericToken(token))
                .filter((token) => !Number.isNaN(token))
        }

        const sequenceA = extractNumericSequence(baseA)
        const sequenceB = extractNumericSequence(baseB)
        const useSequenceSort = (startsWithMA && startsWithMB)
            || (endsWithModA && endsWithModB)
            || (sequenceA.length > 1 && sequenceB.length > 1)

        if (useSequenceSort) {
            const compareLength = Math.min(sequenceA.length, sequenceB.length)
            for (let index = 0; index < compareLength; index += 1) {
                if (sequenceA[index] < sequenceB[index]) {
                    return -1
                }
                if (sequenceA[index] > sequenceB[index]) {
                    return 1
                }
            }

            if (endsWithModA !== endsWithModB) {
                return endsWithModA ? 1 : -1
            }

            if (sequenceA.length < sequenceB.length) {
                return -1
            }
            if (sequenceA.length > sequenceB.length) {
                return 1
            }

            return 0
        }

        if (endsWithModA !== endsWithModB) {
            const modBaseA = baseA.replace(/\bmod$/i, '').trim()
            const modBaseB = baseB.replace(/\bmod$/i, '').trim()

            if (modBaseA.localeCompare(modBaseB, undefined, { numeric: true, sensitivity: 'base' }) === 0) {
                return endsWithModA ? 1 : -1
            }
        }

        return 0
    },
    buildPageUrl(page) {
        const url = new URL(window.location.href)

        if (page === 1) {
            url.searchParams.delete('page')
        } else {
            url.searchParams.set('page', page)
        }

        return url.toString()
    },
    processMainCategoryFilters(data) {
        if (data.nbHits > 0) {
            const category = data.hits[0]
            Object.keys(category[`${this.currentIso}`].main_features).forEach((key) => {

                // dont add it again on subsequent queries if already added from previous category selection
                const existingFilter = this.categoryFilters.find((filter) => filter.name === category[`${this.currentIso}`].main_features[key].name);
                if (!existingFilter) {
                    const mainFeature = {
                        ...category[`${this.currentIso}`].main_features[key],
                        items: [],
                        isMainFilter: true,
                    }
                    this.categoryFilters.push(mainFeature)
                }
            })
        }
    },

    processFacetOptions(data) {
        
        if (Object.keys(data.facets).length > 0) {

            Object.keys(data.facets).forEach((key) => {

                if (key.startsWith(`${this.currentIso}.features.`)) {
                    const innerFacetItems = []

                    Object.keys(data.facets[key]).sort(naturalCompare).sort(this.customSorting).forEach((itemKey) => {
                        innerFacetItems.push({
                            feature: itemKey,
                            number_of_products: data.facets[key][itemKey],
                        })
                    })

                    // check if filter already exists from main filters, if it does only push items without creating new filter
                    const existingFilter = this.categoryFilters.find((filter) => filter.name === key.substring(12));

                    if (existingFilter) {
                        const merged = new Map()
                        const allItems = [...existingFilter.items, ...innerFacetItems]
                        allItems.forEach((item) => merged.set(item.feature, item))
                        existingFilter.items = Array.from(merged.values())
                            .sort((a, b) => naturalCompare(a.feature, b.feature))
                            .sort((a, b) => this.customSorting(a.feature, b.feature))
                    } else {
                        this.categoryFilters.push({
                            position: this.categoryFilters.length + 1,
                            id_feature: this.filteredCategory[0]+"_"+this.categoryFilters.length,
                            name: key.substring(12),
                            items: innerFacetItems,
                            isMainFilter: false,
                        })
                    }
                }
            })
        }
    },

    processProducts(productResults, currentPage, refreshCategoryOptions = false) {
        this.productResults = productResults.hits
        this.hits = productResults.nbHits
        const pricesAndStockIds = []

        this.productResults.forEach((resultItem) => {
            pricesAndStockIds.push({
                id: resultItem.objectID,
                quantity: 1,
                index: pricesAndStockIds.length,
            })
        })

        window.api.getPricesAndStock(pricesAndStockIds).then((productPriceResults) => {
            Object.keys(productPriceResults).forEach((key) => {
                this.productResults[productPriceResults[key].index].stock_and_pricing = productPriceResults[key].data
            })

            const impressionProducts = []
            const impressionProductsGA4 = []

            for (let x = 0; x < this.productResults.length; x += 1) {
                const impressionProduct = this.productResults[x]

                impressionProducts.push({
                    name: impressionProduct[this.currentIso].title,
                    id: impressionProduct.reference,
                    price: impressionProduct.stock_and_pricing ? impressionProduct.stock_and_pricing.price : null,
                    brand: impressionProduct.stock_and_pricing ? impressionProduct.stock_and_pricing.manufacturer_name : null,
                    category: impressionProduct[this.currentIso].category_name,
                    list: this.pageUsedOn,
                    position: x + 1,
                })

                impressionProductsGA4.push({
                    item_name: impressionProduct[this.currentIso].title,
                    item_id: impressionProduct.reference,
                    price: impressionProduct.stock_and_pricing ? impressionProduct.stock_and_pricing.price : null,
                    brand: impressionProduct.stock_and_pricing ? impressionProduct.stock_and_pricing.manufacturer_name : null,
                    category: impressionProduct[this.currentIso].category_name,
                    item_list_name: this.pageUsedOn,
                    item_list_id: this.pageUsedOnId,
                    index: x,
                })
            }

            window.dispatchEvent(new CustomEvent('gtmdata', {
                detail: {
                    ecommerce: {
                        currencyCode: window.Alpine.store('currency').iso_code,
                        impressions: impressionProducts,
                    },
                    event: 'impression',
                },
            }))

            window.dispatchEvent(new CustomEvent('gtmdata', {
                detail: {
                    event: 'view_item_list',
                    ecommerce: {
                        item_list_id: this.pageUsedOnId,
                        item_list_name: this.pageUsedOn,
                        currency: window.Alpine.store('currency').iso_code,
                        currencyCode: window.Alpine.store('currency').iso_code,
                        items: impressionProductsGA4,
                    },
                },
            }))
        }).catch((error) => {
            console.error('Failed to fetch prices and stock:', error)
        }).finally(() => {
            this.loadingProducts = false
            window.dispatchEvent(new CustomEvent('products-loaded'))
        })

        this.visibleProducts = this.productResults

        // Get product history from local storage
        this.visibleProducts.forEach((product) => {
            purchaseHistory.getProductHistory(product.objectID).then((data) => {
                product.productHistory = data
            });
        });

        this.totalProducts = productResults.nbHits
        this.startIndex = (currentPage - 1) * this.pagination.step + 1 // products shown position start
        this.endIndex = this.hits < currentPage * this.pagination.step ? this.hits : currentPage * this.pagination.step // products shown position end

        this.pagination.start = currentPage - 2
        if (this.pagination.start < 1) { this.pagination.start = 1 }
        this.pagination.stop = currentPage + 2
        if (this.pagination.stop > productResults.nbPages) { this.pagination.stop = productResults.nbPages }

        this.pagination.page = productResults.page + 1
        this.pagination.pages = productResults.nbPages

        if (currentPage > this.pagination.pages && this.pagination.pages > 0) {
            this.loadProducts(1, refreshCategoryOptions)
        }

        const pageRange = this.pagination.stop - this.pagination.start + 1
        this.pagination.previousPages = pageRange > 0 ? Array.from(
            Array(pageRange),
            (_, x) => x + this.pagination.start,
        ) : []

        if (this.initialLoad) {
            this.initialLoad = false
        }

        if (this.categoryOptions.length === 1 && this.filteredCategory.length === 0) {
            this.toggleCategorySelection(this.categoryOptions[0].id_category)
        }
    },

    processIndividualFacetOptions(productResults, queryIndex) {
        const facets = productResults?.facets ?? {}
        const filterName = this.categoryFilters[queryIndex].name
        const selectedValues = this.categoryFiltersSelections[`${this.currentIso}.features.${filterName}`] || []

        // Start by preserving selected values
        this.categoryFilters[queryIndex].items = selectedValues.map(value => ({
            feature: value,
            number_of_products: 0,
        }))

        if (Object.keys(facets).length === 0) {
            // No facets returned, but we've already added selected values above
            return
        }

        Object.keys(facets).forEach((facetKey) => {
            if (facetKey.indexOf(`${this.currentIso}.features.`) === 0) {
                // feature facet
                const realKey = facetKey.substring(12)
                this.categoryFilters.forEach((filterEntry, index) => {
                    if (filterEntry.name === realKey) {
                        // Add/update with actual facet results
                        Object.keys(productResults?.facets[facetKey] ?? []).sort(naturalCompare).sort(this.customSorting).forEach((filterOptionKey) => {
                            const existingIndex = this.categoryFilters[index].items.findIndex(item => item.feature === filterOptionKey)
                            if (existingIndex !== -1) {
                                // Update the count for selected items
                                this.categoryFilters[index].items[existingIndex].number_of_products = productResults.facets[facetKey][filterOptionKey]
                            } else {
                                // Add new items
                                filterEntry.items.push({
                                    feature: filterOptionKey,
                                    number_of_products: productResults.facets[facetKey][filterOptionKey],
                                })
                            }
                        })

                        this.categoryFilters[index].items = this.categoryFilters[index].items
                            .sort((a, b) => naturalCompare(a.feature, b.feature))
                            .sort((a, b) => this.customSorting(a.feature, b.feature))
                    }
                })
            }
        })
    },

    createFilterQueriesFromKey(key) {
        const facet = key
        const facets = [key]
        const productCategoryFacets = []

        // at category to filter for
        if (this.filteredCategory.length > 0) {
            this.filteredCategory.forEach((filterId) => {
                productCategoryFacets.push(`category_id:${filterId}`)
            })
        }

        const productFacetFilters = [productCategoryFacets]

        if (this.expressStockOnly) {
            productFacetFilters.push(['in_stock:3'])
        }

        if (this.brand != null) {
            productFacetFilters.push(['manufacturer_name:' + this.brand])
        }

        Object.keys(this.categoryFiltersSelections).forEach((selectionKey) => {
            if (selectionKey !== facet) {
                const entries = []
                this.categoryFiltersSelections[selectionKey].forEach((item) => {
                    entries.push(`${selectionKey}: ${item}`)
                })

                // push in the filters from other categories
                if (entries.length > 0) {
                    productFacetFilters.push(entries)
                }
            }
        })

        return {
            indexName: 'new_products',
            query: this.query,
            restrictSearchableAttributes: [
                'manufacturer_name',
                'reference',
                `${this.currentIso}.title`,
                `${this.currentIso}.meta_keywords`,
                `${this.currentIso}.meta_description`,
                `${this.currentIso}.category_name`,
                `${this.currentIso}.attributes`,
            ],
            params: {
                hitsPerPage: 1,
                page: 0,
                facetFilters: productFacetFilters,
                facets,
            },
        }
    },


    backFillFacetOptions(productResults) {
        const facets = productResults?.facets ?? {}

        if (Object.keys(facets).length === 0) {
            // No facets returned, but we've already added selected values above
            return
        }

        Object.keys(facets).forEach((facetKey) => {
            if (facetKey.indexOf(`${this.currentIso}.features.`) === 0) {
                // feature facet
                const realKey = facetKey.substring(12)
                this.categoryFilters.forEach((filterEntry, index) => {
                    if (filterEntry.name === realKey) {
                        // Add/update with actual facet results
                        Object.keys(productResults?.facets[facetKey] ?? []).sort(naturalCompare).sort(this.customSorting).forEach((filterOptionKey) => {
                            const existingIndex = this.categoryFilters[index].items.findIndex(item => item.feature === filterOptionKey)
                            if (existingIndex !== -1) {
                                // Update the count for selected items
                                this.categoryFilters[index].items[existingIndex].number_of_products = productResults.facets[facetKey][filterOptionKey]
                            } else {
                                // Add new items
                                filterEntry.items.push({
                                    feature: filterOptionKey,
                                    number_of_products: productResults.facets[facetKey][filterOptionKey],
                                })
                            }
                        })

                        this.categoryFilters[index].items = this.categoryFilters[index].items
                            .sort((a, b) => naturalCompare(a.feature, b.feature))
                            .sort((a, b) => this.customSorting(a.feature, b.feature))
                    }
                })
            }
        })
    }
}

export default searchHelper
